import React, { useState, useEffect } from 'react';
import { Card, Form, Switch, InputNumber, Select, Button, message, Tooltip } from 'antd';
import { SaveOutlined, QuestionCircleOutlined } from '@ant-design/icons';
import { attendanceSettingsApi, show422Error, ValidationError } from '../../../utils/api';
import { scrollToFirstError, translateRoleName } from '../../../utils/formHelpers';
import { BulkSelect } from '../../common/bulkSelectProps';

const { Option } = Select;

const AttendanceSettingsTab = ({ data, onUpdate }) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [roles, setRoles] = useState([]);

  useEffect(() => {
    if (data) {
      console.log('📥 AttendanceSettingsTab received data:', data);
      const settings = data.attendance_setting || {};
      const rolesList = data.roles || [];
      
      console.log('📊 Settings:', settings);
      console.log('📊 Roles:', rolesList);
      
      setRoles(rolesList);

      // Parse monthly_report_roles
      let monthlyReportRoles = [];
      try {
        if (data.monthly_report_roles) {
          monthlyReportRoles = Array.isArray(data.monthly_report_roles)
            ? data.monthly_report_roles.map(String)
            : (typeof data.monthly_report_roles === 'string' 
                ? JSON.parse(data.monthly_report_roles) 
                : []);
        }
      } catch (e) {
        console.error('Error parsing monthly_report_roles:', e);
      }

      // Parse ip_addresses
      let ipList = [];
      try {
        if (data.ip_addresses) {
          ipList = Array.isArray(data.ip_addresses) 
            ? data.ip_addresses.filter(ip => ip !== null && ip !== undefined)
            : [];
        }
      } catch (e) {
        console.error('Error parsing ip_addresses:', e);
      }

      console.log('📊 IP Addresses:', ipList);

      const formValues = {
        // فقط فیلدهای مورد نیاز طبق payload
        allow_shift_change: settings.allow_shift_change === 1,
        save_current_location: settings.save_current_location === 1,
        employee_clock_in_out: settings.employee_clock_in_out === 'yes',
        auto_clock_in: settings.auto_clock_in === 'yes',
        auto_clock_in_location: settings.auto_clock_in_location || 'office', // [office, home]
        radius_check: settings.radius_check === 'yes',
        radius: settings.radius || 0,
        show_clock_in_button: settings.show_clock_in_button === 'yes',
        ip_check: settings.ip_check === 'yes',
        ip: ipList,
        monthly_report: settings.monthly_report === 1,
        monthly_report_roles: monthlyReportRoles,
        week_start_from: settings.week_start_from ? String(settings.week_start_from) : '6',
        alert_after_status: (settings.alert_after_status || 0) === 1,
        alert_after: settings.alert_after || 0,
      };
      
      console.log('📝 Setting form values:', formValues);
      form.setFieldsValue(formValues);
    } else {
      console.log('⚠️ No data received in AttendanceSettingsTab');
      form.resetFields();
    }
  }, [data, form]);

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      setLoading(true);

      const payload = {
        allow_shift_change: values.allow_shift_change ? 1 : 0,
        save_current_location: values.save_current_location ? 1 : 0,
        employee_clock_in_out: values.employee_clock_in_out ? 1 : 0,
        auto_clock_in: values.auto_clock_in ? 1 : 0,
        auto_clock_in_location: values.auto_clock_in_location || 'office',
        radius_check: values.radius_check ? 1 : 0,
        radius: values.radius || 0,
        show_clock_in_button: values.show_clock_in_button ? 1 : 0,
        ip_check: values.ip_check ? 1 : 0,
        ip: values.ip || [],
        monthly_report: values.monthly_report ? 1 : 0,
        monthly_report_roles: values.monthly_report ? (values.monthly_report_roles || []).map(Number) : [],
        week_start_from: values.week_start_from ? Number(values.week_start_from) : 6,
        alert_after_status: values.alert_after_status ? 1 : 0,
        alert_after: values.alert_after || 0,
      };

      await attendanceSettingsApi.updateSettings(payload);
      message.success('تنظیمات با موفقیت ذخیره شد');
      if (onUpdate) {
        onUpdate();
      }
    } catch (error) {
      if (error.errorFields && error.errorFields.length > 0) {
        scrollToFirstError(form);
      } else if (error instanceof ValidationError || (error.error && error.error.details)) {
        show422Error(error, form);
      } else {
        message.error(error?.message || 'خطا در ذخیره تنظیمات');
      }
    } finally {
      setLoading(false);
    }
  };

  const weekDays = [
    { label: 'شنبه', value: 6 },
    { label: 'یکشنبه', value: 0 },
    { label: 'دوشنبه', value: 1 },
    { label: 'سه‌شنبه', value: 2 },
    { label: 'چهارشنبه', value: 3 },
    { label: 'پنج‌شنبه', value: 4 },
    { label: 'جمعه', value: 5 },
  ];

  if (!data) {
    return (
      <Card className="bg-white dark:bg-gray-800">
        <div className="flex items-center justify-center py-8">
          <div className="text-gray-500" style={{ fontFamily: 'CustomFont' }}>
            در حال بارگذاری داده‌ها...
          </div>
        </div>
      </Card>
    );
  }

  return (
    <Card className="bg-white dark:bg-gray-800">
      <Form
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
        style={{ fontFamily: 'CustomFont' }}
      >
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="space-y-5">
            <Form.Item
              label={<span style={{ fontFamily: 'CustomFont' }}>اجازه به کارمند برای تغییر شیفت</span>}
              name="allow_shift_change"
              valuePropName="checked"
            >
              <Switch checkedChildren="بله" unCheckedChildren="خیر" />
            </Form.Item>

            <Form.Item
              label={<span style={{ fontFamily: 'CustomFont' }}>ذخیره مکان ورود</span>}
              name="save_current_location"
              valuePropName="checked"
            >
              <Switch checkedChildren="بله" unCheckedChildren="خیر" />
            </Form.Item>

            <Form.Item
              label={<span style={{ fontFamily: 'CustomFont' }}>اجازه ورود و خروج توسط کارمند</span>}
              name="employee_clock_in_out"
              valuePropName="checked"
            >
              <Switch checkedChildren="بله" unCheckedChildren="خیر" />
            </Form.Item>

            <Form.Item
              label={<span style={{ fontFamily: 'CustomFont' }}>ورود خودکار کارمند پس از ورود</span>}
              name="auto_clock_in"
              valuePropName="checked"
            >
              <Switch checkedChildren="بله" unCheckedChildren="خیر" />
            </Form.Item>

            <Form.Item
              label={<span style={{ fontFamily: 'CustomFont' }}>فعال موقعیت مکانی پیش فرض</span>}
              name="auto_clock_in_location"
            >
              <Select>
                <Option value="office">دفتر</Option>
                <Option value="home">خانه</Option>
              </Select>
            </Form.Item>

            <Form.Item
              label={<span style={{ fontFamily: 'CustomFont' }}>بررسی ورود با شعاع موقعیت مکانی ثبت شده</span>}
              name="radius_check"
              valuePropName="checked"
            >
              <Switch checkedChildren="بله" unCheckedChildren="خیر" />
            </Form.Item>

            <Form.Item
              label={<span style={{ fontFamily: 'CustomFont' }}>شعاع (متر)</span>}
              name="radius"
            >
              <InputNumber min={0} style={{ width: '100%' }} />
            </Form.Item>
          </div>

          <div className="space-y-5">
            <Form.Item
              label={<span style={{ fontFamily: 'CustomFont' }}>اجازه ورود خارج از زمان شیفت</span>}
              name="show_clock_in_button"
              valuePropName="checked"
            >
              <Switch checkedChildren="بله" unCheckedChildren="خیر" />
            </Form.Item>

            <Form.Item
              label={<span style={{ fontFamily: 'CustomFont' }}>بررسی ورود با ادرس ip ثبت شده</span>}
              name="ip_check"
              valuePropName="checked"
            >
              <Switch checkedChildren="بله" unCheckedChildren="خیر" />
            </Form.Item>

            <Form.Item
              noStyle
              shouldUpdate={(prevValues, currentValues) => prevValues.ip_check !== currentValues.ip_check}
            >
              {({ getFieldValue }) =>
                getFieldValue('ip_check') ? (
                  <Form.Item
                    label={<span style={{ fontFamily: 'CustomFont' }}>آدرس ایپی</span>}
                    name="ip"
                    tooltip="آدرس‌های IP را با Enter جدا کنید"
                  >
                    <Select
                      mode="tags"
                      placeholder="آدرس IP را وارد کنید و Enter بزنید"
                      tokenSeparators={[',']}
                      style={{ fontFamily: 'CustomFont' }}
                    />
                  </Form.Item>
                ) : null
              }
            </Form.Item>

            <Form.Item
              label={
                <span style={{ fontFamily: 'CustomFont' }}>
                  ارسال ایمیل گزارش حضور ماهیانه{' '}
                  <Tooltip title="ایمیل شامل جزئیات حضور و غیاب همه کارکنان در ماه انتخابی خواهد بود.">
                    <QuestionCircleOutlined className="text-gray-400" />
                  </Tooltip>
                </span>
              }
              name="monthly_report"
              valuePropName="checked"
            >
              <Switch checkedChildren="بله" unCheckedChildren="خیر" />
            </Form.Item>

            <Form.Item
              noStyle
              shouldUpdate={(prevValues, currentValues) => prevValues.monthly_report !== currentValues.monthly_report}
            >
              {({ getFieldValue }) =>
                getFieldValue('monthly_report') ? (
                  <Form.Item
                    label={<span style={{ fontFamily: 'CustomFont' }}>انتخاب نقش ها برای دریافت گزارش ایمیلی</span>}
                    name="monthly_report_roles"
                    rules={[{ required: true, message: 'لطفاً حداقل یک نقش را انتخاب کنید' }]}
                  >
                    <BulkSelect
                      mode="multiple"
                      placeholder="انتخاب نقش‌ها"
                      allValues={roles.map((r) => String(r.id))}
                    >
                      {roles.map((role) => (
                        <Option key={role.id} value={String(role.id)}>
                          {translateRoleName(role.display_name || role.name)}
                        </Option>
                      ))}
                    </BulkSelect>
                  </Form.Item>
                ) : null
              }
            </Form.Item>

            <Form.Item
              label={<span style={{ fontFamily: 'CustomFont' }}>شروع هفته از</span>}
              name="week_start_from"
            >
              <Select>
                {weekDays.map((day) => (
                  <Option key={day.value} value={String(day.value)}>
                    {day.label}
                  </Option>
                ))}
              </Select>
            </Form.Item>

            <Form.Item
              label={<span style={{ fontFamily: 'CustomFont' }}>وضعیت یاداور حضور</span>}
              name="alert_after_status"
              valuePropName="checked"
            >
              <Switch checkedChildren="فعال" unCheckedChildren="غیرفعال" />
            </Form.Item>

            <Form.Item
              label={<span style={{ fontFamily: 'CustomFont' }}>یاداوری پس از چند دقیقه</span>}
              name="alert_after"
            >
              <InputNumber min={0} style={{ width: '100%' }} />
            </Form.Item>
          </div>
        </div>

        <div className="flex justify-end mt-6 pt-4 border-t border-gray-200 dark:border-gray-700">
          <Button
            type="primary"
            icon={<SaveOutlined />}
            htmlType="submit"
            loading={loading}
            className="bg-green-600 hover:bg-green-700 border-0"
            style={{ fontFamily: 'CustomFont' }}
          >
            ذخیره تنظیمات
          </Button>
        </div>
      </Form>
    </Card>
  );
};

export default AttendanceSettingsTab;

